'use client'

import type { ComponentProps } from 'react'

import { cva } from 'class-variance-authority'

import { cn } from '#lib/cn'

import { ButtonVariant } from '../button/types'
import { type SpinnerProps, SpinnerSize, SpinnerVariant } from './types'

const spinnerSizeVariants = cva('', {
  defaultVariants: {
    size: SpinnerSize.Medium,
  },
  variants: {
    size: {
      [SpinnerSize.Small]: 'size-3',
      [SpinnerSize.Medium]: 'size-4',
      [SpinnerSize.Large]: 'size-4.5',
    },
  },
})

const spinnerColorVariants = cva('', {
  defaultVariants: {
    variant: ButtonVariant.Primary,
  },
  variants: {
    variant: {
      [ButtonVariant.Primary]: 'text-text-inverse',
      [ButtonVariant.Secondary]: 'text-text-button-secondary',
      [ButtonVariant.Destructive]: 'text-text-inverse',
      [ButtonVariant.Success]: 'text-text-inverse',
      [ButtonVariant.Ghost]: 'text-text-button-secondary',
    },
  },
})

interface SpinnerBaseProps extends ComponentProps<'svg'> {
  className?: string
}

export function SpinnerBase({ className, ...props }: SpinnerBaseProps) {
  return (
    <svg
      aria-label="Loading"
      // Spin is essential motion: a static spinner reads as frozen and
      // strips progress feedback from sighted users with reduced motion
      // (aria-label only helps screen readers). WCAG/WAI exempts
      // functionally-essential motion from the reduced-motion rule.
      className={cn('size-4 animate-spin', className)}
      fill="none"
      role="status"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}
SpinnerBase.displayName = 'SpinnerBase'

export function Spinner({
  variant = SpinnerVariant.Primary,
  size = SpinnerSize.Medium,
  className,
}: SpinnerProps) {
  return (
    <SpinnerBase
      className={cn(
        spinnerSizeVariants({ size }),
        spinnerColorVariants({ variant }),
        className,
      )}
    />
  )
}
Spinner.displayName = 'Spinner'
